import i18n from './i18n'

const DEFAULT_ACTION_CONTEXT = 'processing your request'
const SERVER_ERROR_MESSAGE = 'Request failed with status code 500'

const actionContextMatchers = [
    { pattern: /clone/i, getActionContext: () => 'cloning your game' },
    { pattern: /export/i, getActionContext: () => 'exporting game data' },
    { pattern: /branding|grey/i, resourceContext: 'game branding' },
    { pattern: /timezone/i, resourceContext: 'timezone data' },
    { pattern: /nubs/i, resourceContext: 'challenge images', itemContext: 'challenge images' },
    { pattern: /npcs/i, resourceContext: 'NPCs', itemContext: 'this NPC' },
    { pattern: /designation/i, resourceContext: 'designations', itemContext: 'this designation' },
    { pattern: /library/i, resourceContext: 'the challenge library' },
    { pattern: /groups/i, resourceContext: 'groups', itemContext: 'this group' },
    { pattern: /teams/i, resourceContext: 'teams', itemContext: 'this team' },
    { pattern: /reward|achievement/i, resourceContext: 'rewards', itemContext: 'this reward' },
    { pattern: /answer/i, resourceContext: 'submissions', itemContext: 'this submission' },
    { pattern: /monitor|stream/i, resourceContext: 'the live stream' },
    { pattern: /challenge|task/i, resourceContext: 'challenges', itemContext: 'this challenge' },
    { pattern: /personalization/i, resourceContext: 'personalizations', itemContext: 'this personalization' },
    { pattern: /games/i, resourceContext: 'your games', itemContext: 'this game' },
]

const methodActionLabels = {
    post: 'saving',
    patch: 'saving',
    put: 'saving',
    delete: 'deleting',
    get: 'loading',
}

const getMatchedActionContext = (match, method) => {
    if (!match) {
        return null
    }

    if (match.getActionContext) {
        return match.getActionContext(method)
    }

    const action = methodActionLabels[method]
    const context = method === 'get' ? match.resourceContext : match.itemContext || match.resourceContext

    return action && context ? `${action} ${context}` : null
}

const getRequestUrl = (error) => error?.config?.url || error?.request?.responseURL || ''

const getInferredActionContext = (error) => {
    const method = error?.config?.method?.toLowerCase()
    const url = getRequestUrl(error)
    const match = actionContextMatchers.find(({ pattern }) => pattern.test(url))
    const matchedContext = getMatchedActionContext(match, method)

    if (matchedContext) {
        return matchedContext
    }

    switch (method) {
        case 'post':
        case 'patch':
        case 'put':
            return 'saving your changes'
        case 'delete':
            return 'deleting this item'
        case 'get':
            return 'loading this data'
        default:
            return DEFAULT_ACTION_CONTEXT
    }
}

export function friendlyMessages(message, actionContext = DEFAULT_ACTION_CONTEXT) {
    const context = actionContext || DEFAULT_ACTION_CONTEXT
    const messages = {
        // Existing app-level validation messages
        'Txt description cannot be blank.': i18n.t('Preview cannot be blank.'),

        // --- System / HTTP error responses ---
        'Request failed with status code 400': i18n.t('Bad request. Please check your input and try again.'),
        'Request failed with status code 401': i18n.t('Unauthorized. Please log in again.'),
        'Request failed with status code 403': i18n.t('You do not have permission to perform this action.'),
        'Request failed with status code 404': i18n.t('The requested resource could not be found.'),
        'Request failed with status code 406': i18n.t('Request not acceptable. Please try a different format.'),
        'Request failed with status code 408': i18n.t('The request timed out. Please try again.'),
        'Request failed with status code 429': i18n.t('Too many requests. Please wait and try again later.'),
        [SERVER_ERROR_MESSAGE]: i18n.t(`The server encountered a problem when ${context}. Please try again later.`),
        'Request failed with status code 501': i18n.t('Server does not support this operation.'),
        'Request failed with status code 502': i18n.t('Bad gateway. The upstream server returned an invalid response.'),
        'Request failed with status code 503': i18n.t('Service unavailable. Please try again in a few minutes.'),
        'Request failed with status code 504': i18n.t('Gateway timeout. The server took too long to respond.'),

        // --- Generic catch-all fallback ---
        'Network Error': i18n.t('Network connection issue. Please check your internet connection and try again.'),
    }

    return messages[message]
}

/**
 * Parses raw API errors and translates them into user-friendly, localized messages.
 * Automatically infers the context of the error (e.g., "saving a challenge") based on 
 * the request URL and HTTP method if an explicit `actionContext` is not provided.
 * 
 * @param {Error} error - The Axios error object.
 * @param {string} [actionContext] - Optional explicit context (e.g., "exporting game data").
 * @returns {string} The human-readable error message.
 */
export function getErrorMessage(error, actionContext) {
    const apiMessage = error?.response?.data?.message || error?.response?.data?.error
    const statusText = error?.response?.statusText
    const message = apiMessage || error?.message || statusText
    const context = actionContext || getInferredActionContext(error)

    if (error?.response?.status === 500 || message === SERVER_ERROR_MESSAGE) {
        return friendlyMessages(SERVER_ERROR_MESSAGE, context)
    }

    return friendlyMessages(message, context) || i18n.t('An unexpected error occurred. Please try again later.')
}
